import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { normalizeAdminApiError } from '../errors';
import { mobileAdminQueryKeys } from '../query';
import { mobileAccountApi } from './api';
import type { MobileAccountAction, MobileAccountActivationData, MobileAccountData } from './types';

export function useMobileAccount(studentId: string, enabled = true) {
    return useQuery({
        queryKey: mobileAdminQueryKeys.account(studentId),
        queryFn: () => mobileAccountApi.get(studentId),
        enabled: enabled && Boolean(studentId),
        retry: false,
    });
}

export function useMobileAccountAction(studentId: string, onActivation: (data: MobileAccountActivationData) => void) {
    const client = useQueryClient();
    return useMutation({
        mutationFn: async ({ action, reason = '' }: { action: MobileAccountAction; reason?: string }) => {
            if (action === 'create') return mobileAccountApi.create(studentId);
            if (action === 'activate') return mobileAccountApi.createActivated(studentId, reason);
            if (action === 'reset') return mobileAccountApi.resetActivation(studentId, reason);
            if (action === 'enable') return mobileAccountApi.enable(studentId, reason);
            if (action === 'disable') return mobileAccountApi.disable(studentId, reason);
            if (action === 'delete') return mobileAccountApi.delete(studentId, reason);
            // Logging out every device is an account recovery operation here:
            // reset activation so the student receives a valid activation OTP,
            // and the API revokes the previous device and sessions together.
            return mobileAccountApi.resetActivation(studentId, reason);
        },
        onSuccess: (response, variables) => {
            if (variables.action === 'delete') {
                client.setQueryData(mobileAdminQueryKeys.account(studentId), undefined);
                client.invalidateQueries({ queryKey: mobileAdminQueryKeys.account(studentId) });
                client.invalidateQueries({ queryKey: mobileAdminQueryKeys.devices() });
                return;
            }
            const data = response.data as MobileAccountData | MobileAccountActivationData;
            client.setQueryData(mobileAdminQueryKeys.account(studentId), { success: true, data: { account: data.account } });
            if ('activationToken' in data) onActivation(data);
            client.invalidateQueries({ queryKey: mobileAdminQueryKeys.devices() });
        },
        onError: normalizeAdminApiError,
    });
}
